import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as api from "./userGroupsAPI";
import { closeModal } from "../modal/modalSlice";
import { removeUserGroupFromUser } from "../users/usersSlice";

const initialState = {
  userGroups: [],
  createUserGroupStatus: null,
  editUserGroupStatus: null,
  deleteUserGroupStatus: 'idle',
  userGroupSelection: null,
  selectedUserGroupMembers: [],
  removingUserFromGroup: {},
  addingUsersToGroup: false,
  hasLoadedUserGroups: false,
  openUserGroupId: null,
  notFoundEmails: [],
};

export const createUserGroup = createAsyncThunk(
  'userGroups/createUserGroup',
  async (params, thunkAPI) => {
    const response = await api.createUserGroup(params);
  
    if (response.isOk) {
      thunkAPI.dispatch(closeModal("createUserGroupModal"));
    }
  
    return response.body;
  }
);
  
export const getUserGroups = createAsyncThunk(
  'userGroups/getUserGroups',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const companyId = state.auth.data?.company_id;
  
    if (companyId === undefined) {
      throw new Error('Company ID is undefined');
    }
  
    const response = await api.getUserGroups(companyId);
    return response.body;
  }
);
  
export const removeUserGroup = createAsyncThunk(
  'userGroups/removeUserGroup',
  async (groupId) => {
    const response = await api.removeUserGroup(groupId);
    return response.body;
  }
);
  
export const editUserGroup = createAsyncThunk(
  'userGroups/editUserGroup',
  async (params, thunkAPI) => {
    const response = await api.editUserGroup(params);
  
    if (response.isOk) {
      thunkAPI.dispatch(closeModal("editUserGroupModal"));
    }
  
    return response.body;
  }
);
  
export const addUsersToGroup = createAsyncThunk(
  'userGroups/addUsersToGroup',
  async (params) => {
    const response = await api.addUsersToGroup(params);
  
    return response.body;
  }
);
  
export const removeUserFromGroup = createAsyncThunk(
  'userGroups/removeUserFromGroup',
  async (params, thunkAPI) => {
    const response = await api.removeUserFromGroup(params);

    if (response.isOk) {
      thunkAPI.dispatch(removeUserGroupFromUser({
        groupId: params.groupId,
        userId: params.userId
      }));
    }
  
    return response.body;
  }
);

export const getInvoicingData = createAsyncThunk(
  'userGroups/getInvoicingData',
  async (groupId) => {
    const response = await api.getInvoicingData(groupId);

    return response.body;
  }
);

export const userGroupsSlice = createSlice({
  name: 'userGroups',
  initialState,
  reducers: {
    clearErrorStatus: (state) => {
      if (state.createUserGroupStatus === 'error') {
        state.createUserGroupStatus = null;
      }
  
      if (state.editUserGroupStatus === 'error') {
        state.editUserGroupStatus = null;
      }
    },
    setSelectedUserGroupMembers: (state, action) => {
      state.selectedUserGroupMembers = action.payload;
    },
    resetUserGroupManageModal: (state) => {
      state.userGroupSelection = null;
      state.selectedUserGroupMembers = [];
      state.priceInfoLoadedForGroupId = null;
      state.invoicingData = null;
    },
    setUserGroupSelection: (state, action) => {
      state.userGroupSelection = action.payload;
    },
    setOpenUserGroupId: (state, action) => {
      state.openUserGroupId = action.payload;
    },
    setNotFoundEmails: (state, action) => {
      state.notFoundEmails = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserGroup.pending, (state) => {
        state.createUserGroupStatus = 'creating';
      })
      .addCase(createUserGroup.fulfilled, (state, action) => {
        state.createUserGroupStatus = 'idle';
        state.userGroups.push(action.payload);
        const value = { id: action.payload.id, name: action.payload.name };
        state.userGroupSelection = { value, label: action.payload.name };
      })
      .addCase(createUserGroup.rejected, (state) => {
        state.createUserGroupStatus = 'error';
      })
      .addCase(getUserGroups.fulfilled, (state, action) => {
        state.userGroups = action.payload;

        // Store userGroups also by id
        state.userGroupsById = action.payload.reduce((acc, group) => {
          acc[group.id] = group;
          return acc;
        }, {});
        
        state.hasLoadedUserGroups = true;
      })
      .addCase(removeUserGroup.pending, (state) => {
        state.deleteUserGroupStatus = 'loading';
      })
      .addCase(removeUserGroup.fulfilled, (state, action) => {
        state.deleteUserGroupStatus = 'idle';
        state.userGroups = state.userGroups.filter(group => group.id !== action.meta.arg);
        state.userGroupSelection = null;
      })
      .addCase(removeUserGroup.rejected, (state) => {
        state.deleteUserGroupStatus = 'error';
      })
      .addCase(editUserGroup.pending, (state) => {
        state.editUserGroupStatus = 'saving';
      })
      .addCase(editUserGroup.fulfilled, (state, action) => {
        state.editUserGroupStatus = 'idle';
        const groupIndex = state.userGroups.findIndex(group => group.id === action.meta.arg.groupId);
  
        if (groupIndex === -1) {
          return;
        }
  
        const newName = action.meta.arg.name;
  
        state.userGroups[groupIndex].name = newName;
      
        state.userGroupSelection.value.name = newName;
        state.userGroupSelection.label = newName;
  
      })
      .addCase(editUserGroup.rejected, (state) => {
        state.editUserGroupStatus = 'error';
      })
      .addCase(addUsersToGroup.pending, (state) => {
        state.addingUsersToGroup = true;
      })
      .addCase(addUsersToGroup.fulfilled, (state, action) => {
        const groupId = action.meta.arg.groupId;
        const userIds = action.meta.arg.userIds;
  
        const groupIndex = state.userGroups.findIndex(group => group.id === groupId);
  
        if (groupIndex === -1) {
          return;
        }
  
        for (let userId of userIds) {
          if (!state.userGroups[groupIndex].userIds.includes(userId)) {
            state.userGroups[groupIndex].userIds.push(userId);
          }
        }
  
        state.selectedUserGroupMembers = [];
        state.addingUsersToGroup = false;
      })
      .addCase(addUsersToGroup.rejected, (state) => {
        state.addingUsersToGroup = false;
      })
      .addCase(removeUserFromGroup.pending, (state, action) => {
        state.removingUserFromGroup[action.meta.arg.userId] = true;
      })
      .addCase(removeUserFromGroup.fulfilled, (state, action) => {
        const groupId = action.meta.arg.groupId;
        const userId = action.meta.arg.userId;
  
        const groupIndex = state.userGroups.findIndex(group => group.id === groupId);
  
        if (groupIndex === -1) {
          return;
        }
  
        const userIndex = state.userGroups[groupIndex].userIds.indexOf(userId);
  
        if (userIndex === -1) {
          return;
        }
  
        state.removingUserFromGroup[userId] = false;
        state.userGroups[groupIndex].userIds.splice(userIndex, 1);
      })
      .addCase(removeUserFromGroup.rejected, (state, action) => {
        state.removingUserFromGroup[action.meta.arg.userId] = false;
      })
      .addCase(getInvoicingData.fulfilled, (state, action) => {
        state.priceInfoLoadedForGroupId = action.meta.arg;
        state.invoicingData = action.payload;
      });
  }
});
  
export const {
  setSelectedUserGroupMembers,
  resetUserGroupManageModal,
  clearErrorStatus,
  setUserGroupSelection,
  setOpenUserGroupId,
  setNotFoundEmails,
} = userGroupsSlice.actions;
  
export const selectUserGroupSelectionValue = (state) => state.userGroups.userGroupSelection?.value || null;
export const selectEmailsNotFound = (state) => state.userGroups.notFoundEmails;
export const selectRemoveUserGroupStatus = (state) => state.userGroups.deleteUserGroupStatus;
  
export const selectUserGroupUserIds = (state, groupId) => {
  const userGroupId = groupId !== undefined ? groupId : state.userGroups.userGroupSelection?.value?.id;
  if (!userGroupId) return [];
  
  const userGroup = state.userGroups.userGroups.find(group => group.id === userGroupId);
  return userGroup?.userIds || [];
}
  
export const selectRemovingUserFromGroup = (state, userId) => {
  return state.userGroups.removingUserFromGroup[userId] ?? false;
}

export const selectUserGroupById = (state, groupId) => {

  if (groupId == null) {
    return null;
  }

  return state.userGroups.userGroups.find(group => group.id === groupId);
}

export const selectUserGroupName = (state, groupId) => {
  return state.userGroups.userGroups.find(group => group.id === groupId)?.name;
}

export const selectUserGroups = (state) => {
  return state.userGroups.userGroups;
}

export const selectUserGroupsById = (state) => {
  return state.userGroups.userGroupsById;
}

export const selectIsPriceInfoShownForSelectedGroup = (state) => {
  const selectedGroupId = state.userGroups.userGroupSelection?.value?.id;

  return selectedGroupId && state.userGroups.priceInfoLoadedForGroupId === selectedGroupId;
}
  
export default userGroupsSlice.reducer;